/**
 * MV（メインビジュアル）のスライダー。
 *
 * デザイン指定（Figma: Doc/SectionHeader 370:1613）:
 *   - 5s程度で横に自動でスライド
 *   - 下部のボタン（タブ）でも対応するスライドに変更可能
 *   - 中身は動画。表示されたら再生（音無し）・ループ再生
 *   - 画面内に表示されていないときは再生停止
 *
 * 実装方針:
 *   - 表示中のスライドの動画だけを再生する（他は一時停止して負荷を抑える）
 *   - 動画は `data-src` で受け取り、表示中と次の1枚にだけ `src` を入れる（#525。
 *     全スライドを先読みすると1本約3MBずつ転送量が増える）
 *   - セクションが画面外に出たら再生と自動送りを止める（IntersectionObserver）
 *   - `prefers-reduced-motion: reduce` のときは自動送りとスライドのアニメーションを止める
 *   - タブは tablist / tab / tabpanel のセマンティクスにして矢印キーでも移動できるようにする
 */
class MvSliderTuna extends HTMLElement {
  connectedCallback() {
    // カスタム要素は子要素の解析前にアップグレードされることがある（script に defer が
    // 無い場合など）。その状態では querySelectorAll が空になるので、DOM の準備を待つ。
    if (!this.querySelector('[data-mv-slide]')) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        window.requestAnimationFrame(() => this.init());
      }
      return;
    }
    this.init();
  }

  init() {
    if (this.initialized) return;
    this.slides = Array.from(this.querySelectorAll('[data-mv-slide]'));
    this.tabs = Array.from(this.querySelectorAll('[data-mv-tab]'));
    this.track = this.querySelector('[data-mv-track]');
    if (this.slides.length === 0) return;
    this.initialized = true;

    this.index = 0;
    this.interval = Number(this.dataset.interval || 5000);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.visible = false;
    this.timer = null;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => {
        this.select(i);
        // 手動で切り替えたら次の自動送りまでの時間を作り直す
        this.restartTimer();
      });
      tab.addEventListener('keydown', (event) => this.onTabKeydown(event, i));
    });

    // 画面内にあるときだけ再生・自動送りする
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.visible = entry.isIntersecting;
          if (this.visible) {
            this.playCurrent();
            this.restartTimer();
          } else {
            this.pauseAll();
            this.stopTimer();
          }
        }
      },
      { threshold: 0.25 }
    );
    this.observer.observe(this);

    this.reduceMotion.addEventListener('change', () => this.restartTimer());

    this.select(0);
  }

  disconnectedCallback() {
    this.stopTimer();
    if (this.observer) this.observer.disconnect();
  }

  select(index) {
    const count = this.slides.length;
    this.index = ((index % count) + count) % count;

    if (this.track) this.track.style.setProperty('--mv-index', String(this.index));

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      // 非表示のスライドは支援技術と Tab 移動から外す
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.tabs.forEach((tab, i) => {
      const active = i === this.index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.loadVideo(this.index);
    this.loadVideo(this.index + 1);

    if (this.visible) this.playCurrent();
  }

  // `data-src` の動画に `src` を入れて読み込みを始める。一度入れたものはそのまま使う
  loadVideo(index) {
    const count = this.slides.length;
    const slide = this.slides[((index % count) + count) % count];
    const video = slide && slide.querySelector('video[data-src]');
    if (!video || video.hasAttribute('src') || !video.dataset.src) return;
    video.preload = 'auto';
    video.src = video.dataset.src;
  }

  playCurrent() {
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === this.index) {
        // ミュートでないと自動再生がブロックされる
        video.muted = true;
        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  pauseAll() {
    this.slides.forEach((slide) => {
      const video = slide.querySelector('video');
      if (video) video.pause();
    });
  }

  restartTimer() {
    this.stopTimer();
    // 動きを減らす設定のときは自動送りしない
    if (this.reduceMotion.matches) return;
    if (!this.visible) return;
    if (this.slides.length < 2) return;
    this.timer = window.setInterval(() => this.select(this.index + 1), this.interval);
  }

  stopTimer() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  onTabKeydown(event, i) {
    const last = this.tabs.length - 1;
    let next = null;
    if (event.key === 'ArrowRight') next = i === last ? 0 : i + 1;
    else if (event.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    this.select(next);
    this.restartTimer();
    this.tabs[next].focus();
  }
}

if (!customElements.get('mv-slider-yokomo')) {
  customElements.define('mv-slider-yokomo', MvSliderTuna);
}
